import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../../lib/api.js'
import { TwemojiImg } from '../../lib/twemoji.jsx'

const PAGE_SIZE = 20

const SOURCE_LABELS = { joueur: 'Joueur', hote: 'Hôte' }
const SOURCE_COLORS = {
  joueur: 'bg-blue-500/20 text-blue-300',
  hote:   'bg-purple-500/20 text-purple-300',
}

function Stars({ note }) {
  if (!note) return <span className="text-white/30 text-xs">—</span>
  return (
    <span className="text-sm tracking-tight" title={`${note}/5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i}>{i < note ? '⭐' : '☆'}</span>
      ))}
    </span>
  )
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function DetailPanel({ feedback }) {
  if (!feedback) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-white/30">
        <div className="text-4xl">💬</div>
        <p className="text-sm">Sélectionnez un avis</p>
      </div>
    )
  }

  const pseudo    = feedback.karts?.pseudo ?? feedback.user_id?.slice(0, 8) ?? '—'
  const vehicule  = feedback.karts?.type_vehicule ?? null
  const deckTitle = feedback.parties?.decks?.titre ?? '—'
  const code      = feedback.parties?.code ?? '—'

  return (
    <div className="space-y-5">
      {/* Auteur + quiz */}
      <div className="flex items-start gap-3">
        {vehicule && <TwemojiImg emoji={vehicule} size={28} className="mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold leading-tight">{pseudo}</p>
          <p className="text-white/40 text-xs mt-0.5 truncate">{deckTitle} — #{code}</p>
        </div>
        <span className="text-white/30 text-xs whitespace-nowrap shrink-0">{formatDate(feedback.cree_le)}</span>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${SOURCE_COLORS[feedback.source]}`}>
          {SOURCE_LABELS[feedback.source]}
        </span>
        <Stars note={feedback.note} />
        {!feedback.lu && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300">
            Non lu
          </span>
        )}
        {feedback.suivi && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-500/20 text-red-300">
            🚩 Suivi
          </span>
        )}
      </div>

      {/* Champs texte */}
      {[
        { label: 'Ce qui a été préféré',      value: feedback.prefere },
        { label: 'Amélioration prioritaire',   value: feedback.amelioration },
        { label: 'Idée de fonctionnalité',     value: feedback.idee_fonctionnalite },
      ].map(({ label, value }) => (
        <div key={label}>
          <p className="text-white/40 text-xs font-medium uppercase tracking-wider mb-1.5">{label}</p>
          <p className={`text-sm rounded-xl px-4 py-3 leading-relaxed ${
            value ? 'text-white bg-white/5' : 'text-white/25 italic'
          }`}>
            {value || 'Non renseigné'}
          </p>
        </div>
      ))}
    </div>
  )
}

function ContextMenu({ feedback, onClose, onToggleLu, onToggleSuivi }) {
  const ref = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div
      ref={ref}
      onClick={e => e.stopPropagation()}
      className="absolute right-0 top-full mt-1 z-50 bg-gray-700 border border-white/15 rounded-xl shadow-2xl overflow-hidden min-w-[180px]"
    >
      <button
        onClick={() => { onToggleLu(); onClose() }}
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-white/80 hover:bg-white/10 hover:text-white transition text-left"
      >
        {feedback.lu ? '📭 Marquer non lu' : '📬 Marquer comme lu'}
      </button>
      <button
        onClick={() => { onToggleSuivi(); onClose() }}
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-white/80 hover:bg-white/10 hover:text-white transition text-left"
      >
        {feedback.suivi ? '🚩 Retirer le suivi' : '🚩 Marquer comme suivi'}
      </button>
    </div>
  )
}

export default function FeedbackManager() {
  const [feedbacks, setFeedbacks]   = useState([])
  const [total, setTotal]           = useState(0)
  const [page, setPage]             = useState(0)
  const [loading, setLoading]       = useState(true)
  const [selectedId, _setSelected]  = useState(null)
  const [menuOpenId, setMenuOpenId] = useState(null)
  const [filtreLu, setFiltreLu]     = useState('all')    // 'all' | 'unread'
  const [filtreSuivi, setFiltreSuivi] = useState('all')  // 'all' | 'suivi'

  // Ref used in the fetch effect to read selectedId without adding it to deps
  const selectedIdRef = useRef(null)
  function selectId(id) {
    selectedIdRef.current = id
    _setSelected(id)
  }

  const totalPages       = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const selectedFeedback = feedbacks.find(f => f.id === selectedId) ?? null

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({ page })
    if (filtreLu   === 'unread') params.set('unread', '1')
    if (filtreSuivi === 'suivi') params.set('suivi',  '1')

    apiFetch(`/api/admin/feedbacks?${params}`)
      .then(data => {
        const list = data.feedbacks ?? []
        setFeedbacks(list)
        setTotal(data.total ?? 0)

        // Auto-sélection : si la sélection courante n'est plus dans la liste,
        // sélectionner le premier élément (et le marquer lu)
        const stillInList = list.some(f => f.id === selectedIdRef.current)
        if (!stillInList && list.length > 0) {
          const first = list[0]
          selectId(first.id)
          if (!first.lu) {
            apiFetch(`/api/admin/feedbacks/${first.id}`, {
              method: 'PATCH', body: JSON.stringify({ lu: true }),
            }).catch(() => {})
            // Mise à jour optimiste
            list[0] = { ...first, lu: true }
            setFeedbacks([...list])
          }
        } else if (!stillInList) {
          selectId(null)
        }
      })
      .catch(() => setFeedbacks([]))
      .finally(() => setLoading(false))
  }, [page, filtreLu, filtreSuivi]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleFiltreLu(val) {
    setFiltreLu(val)
    setPage(0)
  }

  function handleFiltreSuivi(val) {
    setFiltreSuivi(val)
    setPage(0)
  }

  async function handleSelect(fb) {
    selectId(fb.id)
    setMenuOpenId(null)
    if (!fb.lu) {
      apiFetch(`/api/admin/feedbacks/${fb.id}`, {
        method: 'PATCH', body: JSON.stringify({ lu: true }),
      }).catch(() => {})
      setFeedbacks(prev => prev.map(f => f.id === fb.id ? { ...f, lu: true } : f))
    }
  }

  async function handleToggleLu(fb) {
    const newLu = !fb.lu
    apiFetch(`/api/admin/feedbacks/${fb.id}`, {
      method: 'PATCH', body: JSON.stringify({ lu: newLu }),
    }).catch(() => {})
    setFeedbacks(prev => prev.map(f => f.id === fb.id ? { ...f, lu: newLu } : f))
  }

  async function handleToggleSuivi(fb) {
    const newSuivi = !fb.suivi
    apiFetch(`/api/admin/feedbacks/${fb.id}`, {
      method: 'PATCH', body: JSON.stringify({ suivi: newSuivi }),
    }).catch(() => {})
    setFeedbacks(prev => prev.map(f => f.id === fb.id ? { ...f, suivi: newSuivi } : f))
  }

  return (
    <div className="flex gap-4 items-start">

      {/* ── Panneau gauche — liste ──────────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-4">

        {/* En-tête */}
        <div>
          <h1 className="text-xl font-bold text-white">Avis reçus</h1>
          <p className="text-white/40 text-sm mt-0.5">
            {total} retour{total > 1 ? 's' : ''} au total
          </p>
        </div>

        {/* Barre de filtres */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filtre lecture */}
          <div className="flex items-center gap-1 bg-gray-800/50 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => handleFiltreLu('unread')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filtreLu === 'unread'
                  ? 'bg-amber-500/20 text-amber-300'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              📭 Non lus
            </button>
            <button
              onClick={() => handleFiltreLu('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filtreLu === 'all'
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              Tous
            </button>
          </div>

          {/* Filtre suivi */}
          <div className="flex items-center gap-1 bg-gray-800/50 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => handleFiltreSuivi('suivi')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filtreSuivi === 'suivi'
                  ? 'bg-red-500/20 text-red-300'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              🚩 Suivi
            </button>
            <button
              onClick={() => handleFiltreSuivi('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filtreSuivi === 'all'
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              Tous
            </button>
          </div>
        </div>

        {/* Contenu */}
        {loading ? (
          <div className="flex justify-center py-16">
            <svg className="animate-spin h-8 w-8 text-white/40" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-white/40">
            <div className="text-5xl">💬</div>
            <p className="font-medium">Aucun avis trouvé.</p>
          </div>
        ) : (
          <>
            {/* Liste */}
            <div className="bg-gray-800/50 border border-white/10 rounded-2xl">
              {feedbacks.map((fb, idx) => {
                const pseudo    = fb.karts?.pseudo ?? (fb.user_id ? fb.user_id.slice(0, 8) + '…' : '—')
                const vehicule  = fb.karts?.type_vehicule ?? null
                const deckTitle = fb.parties?.decks?.titre ?? '—'
                const isSelected = fb.id === selectedId
                const hasTexts   = fb.prefere || fb.amelioration || fb.idee_fonctionnalite
                const isFirst    = idx === 0
                const isLast     = idx === feedbacks.length - 1

                return (
                  <div
                    key={fb.id}
                    onClick={() => handleSelect(fb)}
                    className={`relative flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0 cursor-pointer transition-colors
                      ${isFirst ? 'rounded-t-2xl' : ''}
                      ${isLast  ? 'rounded-b-2xl' : ''}
                      ${isSelected ? 'bg-blue-500/10' : 'hover:bg-white/5'}
                    `}
                  >
                    {/* Pastille non lu */}
                    <div className={`w-2 h-2 rounded-full shrink-0 transition-colors ${!fb.lu ? 'bg-amber-400' : 'bg-transparent'}`} />

                    {/* Source */}
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 ${SOURCE_COLORS[fb.source]}`}>
                      {SOURCE_LABELS[fb.source]}
                    </span>

                    {/* Info principale */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${!fb.lu ? 'font-semibold text-white' : 'font-normal text-white/80'}`}>
                        {deckTitle}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {vehicule && <TwemojiImg emoji={vehicule} size={12} />}
                        <span className="text-white/40 text-xs truncate">{pseudo}</span>
                        {hasTexts && <span className="text-blue-400/60 text-xs">· 💬</span>}
                      </div>
                    </div>

                    {/* Note */}
                    <div className="shrink-0">
                      <Stars note={fb.note} />
                    </div>

                    {/* Bouton suivi */}
                    <button
                      title={fb.suivi ? 'Retirer le suivi' : 'Marquer comme suivi'}
                      onClick={e => { e.stopPropagation(); handleToggleSuivi(fb) }}
                      className={`p-1 rounded transition text-base leading-none shrink-0 ${
                        fb.suivi ? 'opacity-100' : 'opacity-20 hover:opacity-60'
                      }`}
                    >
                      🚩
                    </button>

                    {/* Menu ⋮ */}
                    <div className="relative shrink-0">
                      <button
                        onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === fb.id ? null : fb.id) }}
                        className="w-7 h-7 flex items-center justify-center text-lg text-white/30 hover:text-white hover:bg-white/10 rounded-lg transition"
                        title="Options"
                      >
                        ⋮
                      </button>
                      {menuOpenId === fb.id && (
                        <ContextMenu
                          feedback={fb}
                          onClose={() => setMenuOpenId(null)}
                          onToggleLu={() => handleToggleLu(fb)}
                          onToggleSuivi={() => handleToggleSuivi(fb)}
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-sm text-white/60 hover:text-white disabled:opacity-30 border border-white/20 rounded-lg hover:bg-white/10 transition disabled:cursor-not-allowed"
                >
                  ← Préc.
                </button>
                <span className="text-white/40 text-sm">Page {page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 text-sm text-white/60 hover:text-white disabled:opacity-30 border border-white/20 rounded-lg hover:bg-white/10 transition disabled:cursor-not-allowed"
                >
                  Suiv. →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Panneau droit — détail ──────────────────────────────────── */}
      <div className="w-80 xl:w-96 shrink-0 sticky top-0 max-h-[calc(100vh-5rem)] overflow-y-auto bg-gray-800/50 border border-white/10 rounded-2xl p-5">
        <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">Détail</p>
        <DetailPanel feedback={selectedFeedback} />
      </div>

    </div>
  )
}
